import { DIMENSIONS, type Dimension, type Report, type Scene } from './model';

const dimList = Object.keys(DIMENSIONS) as Dimension[];

function polarPoint(index: number, radius: number, count = dimList.length) {
  const angle = -Math.PI / 2 + (index / count) * Math.PI * 2;
  return [160 + Math.cos(angle) * radius, 160 + Math.sin(angle) * radius] as const;
}

export function CompassChart({ report, compact = false }: { report: Report; compact?: boolean }) {
  const points = dimList.map((d, i) => polarPoint(i, 24 + report.scores[d] * 0.96).join(',')).join(' ');
  return (
    <div className={`compass-wrap ${compact ? 'compact' : ''}`}>
      <svg viewBox="0 0 320 320" role="img" aria-label="十维天赋罗盘">
        {[32, 64, 96, 120].map((r) => <circle key={r} cx="160" cy="160" r={r} className="grid-ring" />)}
        {dimList.map((d, i) => {
          const [x, y] = polarPoint(i, 120);
          return <line key={d} x1="160" y1="160" x2={x} y2={y} className="grid-axis" />;
        })}
        <polygon points={points} className="compass-fill" />
        {dimList.map((d, i) => {
          const [x, y] = polarPoint(i, 139);
          return <g key={d}><circle cx={polarPoint(i, 24 + report.scores[d] * .96)[0]} cy={polarPoint(i, 24 + report.scores[d] * .96)[1]} r="4" fill={DIMENSIONS[d].color} /><text x={x} y={y} textAnchor="middle" dominantBaseline="middle">{DIMENSIONS[d].name}</text></g>;
        })}
        <circle cx="160" cy="160" r="24" className="prism-core" />
        <text x="160" y="157" textAnchor="middle" className="core-label">{report.mode}</text>
        <text x="160" y="174" textAnchor="middle" className="core-score">{report.consistency}%</text>
      </svg>
    </div>
  );
}

export function StarMap({ report }: { report: Report }) {
  const positions = [[80, 74], [248, 82], [105, 226], [235, 220]];
  const sizes = report.top.map((d) => 20 + report.scores[d] * .16);
  return (
    <svg viewBox="0 0 330 290" className="star-map" role="img" aria-label="优势组合星图">
      <path d="M18 154 C92 110, 212 200, 312 118" className="contour-line" />
      <path d="M12 190 C112 142, 214 256, 320 180" className="contour-line faint" />
      {positions.slice(1).map(([x, y], i) => <line key={i} x1={positions[0][0]} y1={positions[0][1]} x2={x} y2={y} className="star-link" />)}
      {report.top.map((d, i) => <g key={d} transform={`translate(${positions[i][0]} ${positions[i][1]})`}>
        <circle r={sizes[i]} fill={DIMENSIONS[d].color} opacity={i < 2 ? .94 : .48} />
        <circle r={sizes[i] + 7} className="star-orbit" />
        <text y="4" textAnchor="middle" className="star-label">{DIMENSIONS[d].name}</text>
        <text y={sizes[i] + 24} textAnchor="middle" className="star-score">{report.scores[d]}</text>
      </g>)}
    </svg>
  );
}

export function TerrainProfile({ report }: { report: Report }) {
  const values = report.ranking.map((d, i) => ({ d, x: 22 + i * 31, y: 166 - report.scores[d] * 1.12 }));
  const path = values.map((v, i) => `${i ? 'L' : 'M'}${v.x},${v.y}`).join(' ');
  const area = `${path} L${values.at(-1)!.x},180 L${values[0].x},180 Z`;
  return <svg viewBox="0 0 330 220" className="terrain" role="img" aria-label="天赋地形剖面">
    {[50, 100, 150].map((y) => <line key={y} x1="16" y1={y} x2="314" y2={y} className="terrain-grid" />)}
    <path d={area} className="terrain-area" />
    <path d={path} className="terrain-path" />
    {values.map((v) => <g key={v.d}><circle cx={v.x} cy={v.y} r="4" fill={DIMENSIONS[v.d].color} /><text x={v.x} y="201" textAnchor="middle" className="terrain-label">{DIMENSIONS[v.d].name.slice(0, 1)}</text></g>)}
  </svg>;
}

export function SceneHeat({ report }: { report: Report }) {
  const scenes = Object.entries(report.sceneScores) as [Scene, number][];
  return <div className="heat-map" role="img" aria-label="五类场景能量热区">
    {scenes.map(([scene, score]) => <div className="heat-row" key={scene}>
      <span>{scene}</span>
      <div className="heat-track"><i style={{ width: `${score}%` }} /></div>
      <strong>{score}</strong>
    </div>)}
  </div>;
}
